import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from './AuthProvider'

export interface Workspace {
  id: string
  name: string
  timezone: string
  owner_id: string
}

interface WorkspaceContextValue {
  workspace: Workspace | null
  workspaces: Workspace[]
  workspaceId: string | null
  loading: boolean
  error: Error | null
  selectWorkspace: (id: string) => void
  refetch: () => void
}

const STORAGE_KEY = 'socialhub.workspace'
const WorkspaceContext = createContext<WorkspaceContextValue | null>(null)

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const [selectedId, setSelectedId] = useState<string | null>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY)
    } catch {
      return null
    }
  })

  const query = useQuery({
    queryKey: ['workspaces', user?.id],
    enabled: Boolean(user?.id),
    queryFn: async (): Promise<Workspace[]> => {
      const { data, error } = await supabase
        .from('workspaces')
        .select('id, name, timezone, owner_id')
        .order('created_at', { ascending: true })
      if (error) throw new Error(error.message)
      return data ?? []
    },
  })

  const workspaces = useMemo(() => query.data ?? [], [query.data])

  // Garante que sempre há um workspace ativo válido.
  useEffect(() => {
    if (workspaces.length === 0) return
    const stillValid = selectedId && workspaces.some((w) => w.id === selectedId)
    if (!stillValid) {
      setSelectedId(workspaces[0].id)
    }
  }, [workspaces, selectedId])

  useEffect(() => {
    if (!selectedId) return
    try {
      localStorage.setItem(STORAGE_KEY, selectedId)
    } catch {
      // storage indisponível: seleção vale só para a sessão atual
    }
  }, [selectedId])

  const workspace = useMemo(
    () => workspaces.find((w) => w.id === selectedId) ?? workspaces[0] ?? null,
    [workspaces, selectedId],
  )

  const value: WorkspaceContextValue = {
    workspace,
    workspaces,
    workspaceId: workspace?.id ?? null,
    loading: query.isPending && Boolean(user?.id),
    error: query.error as Error | null,
    selectWorkspace: setSelectedId,
    refetch: () => void query.refetch(),
  }

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>
}

export function useWorkspace(): WorkspaceContextValue {
  const context = useContext(WorkspaceContext)
  if (!context) {
    throw new Error('useWorkspace precisa estar dentro de <WorkspaceProvider>.')
  }
  return context
}
